// <T> — auto-translate wrapper for hardcoded English/Italian strings.
//
// Usage:
//   <T>Loading guilds...</T>
//   <T className="text-red-400">Join guild</T>
//
// Behavior:
//   - Reads i18n.language. When "it" (source) it renders the child untouched.
//   - When lang is en/de/fil/ru, checks the process-wide cache, then falls back
//     to `api.translateTexts` (Gemini 3 Flash + Mongo cache).
//   - Batches every <T> mounted within the same 40ms window into ONE round-trip.
//
// Notes:
//   - Only translates plain-text children (single string). Ignore React nodes.
//   - Placeholders shown while pending: the source text (feels snappy).
//   - Safe: swallowing every error keeps the app working even if the LLM key
//     is missing or the endpoint 500s.
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";

// Process-wide cache: { lang: { source: translation } }.
const memory = { it: {}, en: {}, de: {}, fil: {}, ru: {} };
// Pending queue awaiting the next flush.
let queue = new Set();
let waiters = [];
let timer = null;

function schedule(lang) {
  if (timer) return;
  timer = setTimeout(() => flush(lang), 40);
}

async function flush(lang) {
  timer = null;
  const batch = Array.from(queue);
  queue.clear();
  const cbs = waiters;
  waiters = [];
  if (batch.length === 0) return;
  try {
    const r = await api.translateTexts(batch, lang);
    const t = r?.translations || {};
    Object.entries(t).forEach(([src, dst]) => { memory[lang][src] = dst; });
  } catch (e) { /* silent */ }
  cbs.forEach(cb => cb());
}

export default function T({ children, className, as: Tag = "span", ...rest }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").toLowerCase();
  const src = typeof children === "string" ? children : null;
  const [tick, setTick] = useState(0);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    if (!src || lang === "it" || memory[lang]?.[src]) return;
    queue.add(src);
    waiters.push(() => { if (mounted.current) setTick(x => x + 1); });
    schedule(lang);
  }, [src, lang, tick]);
  if (!src) return <Tag className={className} {...rest}>{children}</Tag>;
  const out = lang === "it" ? src : (memory[lang]?.[src] || src);
  return <Tag className={className} {...rest}>{out}</Tag>;
}
